import React, { useState, useRef, useEffect } from 'react';
import { customEmojis, CustomEmoji } from '../../utils/emojiMapping';
import SmileIcon from '../../assets/emojis/big_bright_smile.gif';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: CustomEmoji) => void;
  isDesktop?: boolean;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, isDesktop = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pickerPosition, setPickerPosition] = useState<{
    left: number | 'auto';
    right: number | 'auto';
  }>({ left: 0, right: 'auto' });
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const calculatePickerPosition = () => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    if (isDesktop) {
      const pickerWidth = 400;
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      const pickerLeft = buttonCenter - pickerWidth / 2 + pickerWidth * 0.5;

      if (pickerLeft < 10) {
        setPickerPosition({ left: 10 - buttonRect.left, right: 'auto' });
      } else if (pickerLeft + pickerWidth > viewportWidth - 10) {
        setPickerPosition({ left: 'auto', right: 10 });
      } else {
        const offset = -(pickerWidth / 2) + buttonRect.width / 2 + pickerWidth * 0.5;
        setPickerPosition({ left: offset, right: 'auto' });
      }
    } else {
      const margin = 20;
      const pickerWidth = viewportWidth - margin * 2;
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      const pickerLeft = buttonCenter - pickerWidth / 2;

      setPickerPosition({ left: margin - buttonRect.left, right: 'auto' });
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculatePickerPosition();

      const handleResize = () => {
        calculatePickerPosition();
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, isDesktop]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredEmojis = customEmojis.filter(
    (emoji) =>
      emoji.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emoji.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleEmojiClick = (emoji: CustomEmoji) => {
    onEmojiSelect(emoji);
    setIsOpen(false);
    setSearchTerm('');
  };

  const togglePicker = () => {
    setIsOpen(!isOpen);
    setSearchTerm('');
  };

  const getPickerWidth = () => {
    if (isDesktop) {
      return '400px';
    } else {
      const margin = 20;
      return `${window.innerWidth - margin * 2}px`;
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Кнопка для открытия пикера */}
      <button
        ref={buttonRef}
        type='button'
        onClick={togglePicker}
        style={{
          background: isDesktop ? 'transparent' : '#00000000',
          border: 'none',
          cursor: 'pointer',
          padding: isDesktop ? '12px' : '8px',
          color: '#000',
          fontFamily: 'monospace',
          fontSize: isDesktop ? '14px' : '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isDesktop ? 'auto' : '44px',
          minWidth: isDesktop ? 'auto' : '44px',
          borderRadius: isDesktop ? '0' : '22px',
          transition: 'all 0.2s ease',
        }}
      >
        {isDesktop ? (
          <img
            src={SmileIcon}
            alt='Эмодзи'
            style={{
              width: '24px',
              height: '24px',
              objectFit: 'contain',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = '😊';
            }}
          />
        ) : (
          <img
            src={SmileIcon}
            alt='Эмодзи'
            style={{
              width: '26px',
              height: '26px',
              objectFit: 'contain',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = '🙂';
            }}
          />
        )}
      </button>

      {/* Выпадающий список эмодзи */}
      {isOpen && (
        <div
          ref={pickerRef}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: pickerPosition.left === 'auto' ? 'auto' : `${pickerPosition.left}px`,
            right: pickerPosition.right === 'auto' ? 'auto' : `${pickerPosition.right}px`,
            marginBottom: '8px',
            backgroundColor: '#fff',
            border: isDesktop ? '2px solid #000' : '1px solid #ccc',
            borderRadius: isDesktop ? '0' : '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            width: getPickerWidth(),
            maxHeight: '300px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Поле поиска */}
          <div
            style={{
              padding: '12px',
              borderBottom: isDesktop ? '2px solid #000' : '1px solid #eee',
            }}
          >
            <input
              type='text'
              placeholder='Поиск эмодзи...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: isDesktop ? '2px solid #000' : '1px solid #ccc',
                borderRadius: isDesktop ? '0' : '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Список эмодзи */}
          <div
            style={{
              padding: '8px',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
              gap: '4px',
              maxHeight: '220px',
            }}
          >
            {filteredEmojis.length === 0 ? (
              <div
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '20px',
                  color: '#666',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                }}
              >
                Эмодзи не найдены
              </div>
            ) : (
              filteredEmojis.map((emoji) => (
                <button
                  key={emoji.id}
                  onClick={() => handleEmojiClick(emoji)}
                  title={emoji.description}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '8px',
                    cursor: 'pointer',
                    borderRadius: isDesktop ? '0' : '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease',
                    minHeight: '42px',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  }}
                >
                  <img
                    src={emoji.src}
                    alt={emoji.description}
                    style={{
                      width: '24px',
                      height: '24px',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
